import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth, useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex, Input, Switch } from '@molecule/app-ui-react'

import { MoleculeSpinner } from '../components/MoleculeSpinner.js'
import { useIsMobile, useMediaQuery } from '../hooks/useMediaQuery.js'
import { emitNotification } from '../notifications.js'
import { addPaymentMethodLabel } from '../upgrade-copy.js'
import { reportHandledFailure } from '../utilities/error-reporting.js'
import {
  autoRefillFrom,
  canEnableUsageBilling,
  chargedThisPeriodCents,
  formatCentsCompact,
  isGrantedSeatPlan,
  overageCentsThisPeriod,
  parseDollarsToCents,
  parseSpendCapDollars,
  parseTopUpAmountDollars,
  parseTopUpDollars,
  resolvedSpendCapCents,
  runBillingAction,
  runSpendCapAction,
  runTopUpAction,
  setAutoRefillAction,
  setSpendCapAction,
  setUsageBillingAction,
  spendCapIsCustom,
  topUpAction,
  type TopUpFailure,
  topUpFailureFrom,
  type TopUpFailureReason,
  topUpLimitsFrom,
  usageBalanceCents,
  usageBillingEnabledFrom,
  type UsageResponseWithBilling,
} from '../utilities/usage-billing-utilities.js'
import {
  brokerLabel,
  type BrokerUsageLine,
  formatCents,
  formatStorage,
  formatUsageLabel,
  LIMIT_STATE_COLOR,
  type LimitState,
  limitState,
  totalBrokerRequests,
  totalBrokerUnits,
  usagePercent,
} from '../utilities/usage-utilities.js'

/**
 * Billing / usage building blocks — the account's plan, extra usage + spend
 * cap, and usage-vs-limits meters from `GET /account/usage`.
 *
 * The standalone `/billing` PAGE was retired (its route redirects to the
 * dashboard): the same content now lives in the user settings panel (Billing +
 * Usage sections) and the in-IDE upgrade modal, both composed from the pieces
 * exported here — {@link useAccountUsage}, {@link PlanSummary},
 * {@link ExtraUsageControls}, {@link OverLimitBanner},
 * {@link ResourceUsageBars}, {@link AiUsageMeter}, {@link BrokerUsage} — so
 * the surfaces can never drift apart. {@link BillingContent} is the modal's
 * full-page-style composition.
 *
 * All percent / limit / state math lives in `utilities/usage-utilities.ts` so
 * it stays unit-testable; these components are thin presentational shells.
 * Styling: ClassMap for typography + layout helpers, inline styles + CSS vars
 * for the bespoke bars/cards.
 */

/**
 * Height of the bond's `<Input size="sm">` (its cva resolves to `h-[35px]`).
 * A `<Button size="sm">` is `h-[26px]`, so any button sharing a row with one of
 * those inputs must be pinned here or the row renders 9px ragged. The coarse
 * `touchTargetCompact` floor (36px) then lifts both together.
 */
const SM_INPUT_HEIGHT = 35

const PLAN_LABEL: Record<string, string> = {
  anonymous: 'Guest',
  stripe: 'Pro',
  stripeMonthly: 'Pro',
  stripeYearly: 'Pro',
  team: 'Team',
  teamMonthly: 'Team',
  teamYearly: 'Team',
}

/**
 * The line to show for a refused top-up when the server answered without one of
 * its own — the four bounds/eligibility refusals the client normally prevents,
 * plus anything unrecognized. Every reason the server DOES write a sentence for
 * (`no-customer`, `card-declined`, `requires-action`, the two caps) renders that
 * sentence instead; these are the honest last resort, not a substitute for it.
 *
 * @param t - The translate function.
 * @param reason - The server's refusal reason.
 * @returns The localized line.
 */
function topUpFailureFallback(
  t: ReturnType<typeof useTranslation>['t'],
  reason: TopUpFailureReason,
): string {
  switch (reason) {
    case 'no-customer':
      return t('billing.balance.errorNoCustomer', undefined, {
        defaultValue: 'Add a payment method before adding funds.',
      })
    case 'card-declined':
      return t('billing.balance.errorDeclined', undefined, {
        defaultValue: 'Your card was declined.',
      })
    case 'requires-action':
      return t('billing.balance.errorRequiresAction', undefined, {
        defaultValue: 'Your bank needs you to confirm this payment.',
      })
    case 'velocity-cap-reached':
      return t('billing.balance.errorVelocityCap', undefined, {
        defaultValue: 'Too many top-ups this billing period. Contact support to continue.',
      })
    case 'period-cap-reached':
      return t('billing.balance.errorPeriodCap', undefined, {
        defaultValue: 'Your monthly spend cap has been reached. Raise the cap below to add funds.',
      })
    case 'not-eligible':
      return t('billing.balance.errorNotEligible', undefined, {
        defaultValue: 'This account cannot add funds.',
      })
    default:
      return t('billing.balance.errorRequest', undefined, {
        defaultValue: 'Could not add funds. Check your card and try again.',
      })
  }
}

/** Human label for the effective plan key (null → Free). */
function planLabel(plan: string | null): string {
  if (!plan) return 'Free'
  return (
    PLAN_LABEL[plan] ??
    (plan.startsWith('team') ? 'Team' : plan.startsWith('stripe') ? 'Pro' : plan)
  )
}

// ONE meter treatment for every bar (resource limits, spend cap, AI usage,
// brokered usage): 8px bordered `surface-secondary` track, readable in both
// themes.
const meterTrackStyle = {
  height: 8,
  background: 'var(--color-surface-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  overflow: 'hidden',
} as const

const meterFillStyle = {
  height: '100%',
  borderRadius: 4,
  transition: 'width 0.3s',
} as const

/** The /billing-style card chrome, shared by the modal composition. */
const cardStyle = {
  border: '1px solid var(--color-border)',
  borderRadius: 12,
  padding: 20,
  marginBottom: 24,
} as const

/** Minimum gap between focus/visibility refetches of `/account/usage`. */
export const USAGE_FOCUS_REFETCH_MIN_MS = 10_000

/** Poll interval for `/account/usage` while part of the balance is held by running work. */
export const USAGE_HELD_POLL_MS = 30_000

/**
 * Fetch + state for `GET /account/usage` — one instance per surface (settings
 * panel, upgrade modal), so each owns its refresh lifecycle.
 *
 * The payload goes stale on its own: a hold for work in progress is released
 * when the turn ends, and a refund webhook changes the balance server-side. So
 * besides the mount read it refetches when the tab becomes visible or the
 * window gains focus (at most once per {@link USAGE_FOCUS_REFETCH_MIN_MS}), and
 * polls every {@link USAGE_HELD_POLL_MS} while `heldCents > 0`. A background
 * refetch that fails keeps the data already shown instead of swapping the page
 * for the load error.
 *
 * @returns The usage payload, loading/error state, and a `reload`.
 */
export function useAccountUsage(): {
  usage: UsageResponseWithBilling | null
  loading: boolean
  error: boolean
  reload: () => Promise<void>
} {
  const http = useHttpClient()
  const [usage, setUsage] = useState<UsageResponseWithBilling | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const hasUsageRef = useRef(false)
  const lastFetchRef = useRef(0)

  const load = useCallback(
    async (background: boolean) => {
      lastFetchRef.current = Date.now()
      if (!background) setError(false)
      try {
        const res = await http.get<UsageResponseWithBilling>('/account/usage')
        const next = res.data ?? null
        // Guard the shape: a truthy-but-partial payload (proxy error page, test
        // stub, half-deployed API) must read as "no data", not crash a consumer
        // dereferencing usage.period/usage.limits.
        const valid = next && next.period && next.limits ? next : null
        hasUsageRef.current = valid !== null
        setUsage(valid)
        setError(false)
      } catch (error) {
        console.error('Failed to load account usage', error)
        if (!background || !hasUsageRef.current) setError(true)
      }
      setLoading(false)
    },
    [http],
  )

  const reload = useCallback(() => load(false), [load])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    const refetchIfStale = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      if (Date.now() - lastFetchRef.current < USAGE_FOCUS_REFETCH_MIN_MS) return
      void load(true)
    }
    document.addEventListener('visibilitychange', refetchIfStale)
    window.addEventListener('focus', refetchIfStale)
    return () => {
      document.removeEventListener('visibilitychange', refetchIfStale)
      window.removeEventListener('focus', refetchIfStale)
    }
  }, [load])

  const held = usage ? heldCentsFrom(usage) > 0 : false
  useEffect(() => {
    if (!held) return
    const id = window.setInterval(() => void load(true), USAGE_HELD_POLL_MS)
    return () => window.clearInterval(id)
  }, [held, load])

  return { usage, loading, error, reload }
}

function UsageBar({
  label,
  value,
  limit,
  unit,
  storage,
  money,
}: {
  label: string
  value: number
  limit: number
  unit?: string
  /** When true, render value/limit using the GB-promoting storage formatter. */
  storage?: boolean
  /** When true, value/limit are cents — render as dollars. */
  money?: boolean
}) {
  const pct = usagePercent(value, limit)
  const state: LimitState = limitState(value, limit)
  const color = LIMIT_STATE_COLOR[state]

  const valueText = storage
    ? `${formatStorage(value)} / ${limit >= 999999 || !Number.isFinite(limit) || limit < 0 ? 'Unlimited' : formatStorage(limit)}`
    : money
      ? `${formatCents(value)} / ${formatCents(limit)}`
      : formatUsageLabel(value, limit, unit)

  return (
    <div
      style={{ marginBottom: 14 }}
      data-mol-id={`usage-bar-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Flex align="center" justify="between" style={{ fontSize: 13, marginBottom: 4 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span
          style={{
            color:
              state === 'over'
                ? 'var(--mol-color-error, #ef4444)'
                : state === 'approaching'
                  ? // Readable warning TEXT in both themes (P5-10 sweep) — the old hardcoded
                    // #eab308 was unreadable on light backgrounds.
                    'var(--color-warning-text, #7a6200)'
                  : 'var(--mol-color-text-secondary, #aaaaaa)',
            fontWeight: state === 'ok' || state === 'unlimited' ? 400 : 600,
          }}
        >
          {valueText}
        </span>
      </Flex>
      <div style={meterTrackStyle}>
        <div style={{ ...meterFillStyle, width: `${pct}%`, background: color }} />
      </div>
    </div>
  )
}

/**
 * The date the current billing period ends — the one "resets on" date every
 * part of the page shows.
 *
 * `period.end` is the EXCLUSIVE end instant. A calendar-month (or lapsed-plan)
 * period ends at 00:00 UTC on the 1st, which local formatting west of UTC shows
 * as the day before ("Oct 31" for a 1 Nov reset). An end at UTC midnight is
 * therefore a day boundary and is formatted in UTC, so it reads as the day the
 * new period starts; a subscription-anchored end at any other time is a real
 * moment and keeps the viewer's local date.
 *
 * @param usage - The loaded usage payload.
 * @returns The localized reset date of `usage.period`.
 */
export function periodResetDate(usage: Pick<UsageResponseWithBilling, 'period'>): string {
  const end = new Date(usage.period.end)
  if (Number.isNaN(end.getTime())) return ''
  const atUtcMidnight =
    end.getUTCHours() === 0 &&
    end.getUTCMinutes() === 0 &&
    end.getUTCSeconds() === 0 &&
    end.getUTCMilliseconds() === 0
  return atUtcMidnight
    ? end.toLocaleDateString(undefined, { timeZone: 'UTC' })
    : end.toLocaleDateString()
}

/**
 * Cents of the balance held by work still running (`heldCents` from
 * `GET /account/usage`); 0 when absent.
 *
 * @param usage - The loaded usage payload.
 * @returns Held cents (`>= 0`).
 */
function heldCentsFrom(usage: UsageResponseWithBilling): number {
  const held = (usage as UsageResponseWithBilling & { heldCents?: unknown }).heldCents
  return typeof held === 'number' && Number.isFinite(held) && held > 0 ? held : 0
}

/**
 * The current plan: small "Current plan" label over the large plan name, plus
 * the date the period resets. Card chrome is the host's.
 *
 * @param props - The loaded usage payload.
 */
export function PlanSummary({ usage }: { usage: UsageResponseWithBilling }) {
  const { t } = useTranslation()
  return (
    <div data-mol-id="billing-plan-summary">
      <div style={{ fontSize: 12, opacity: 0.5, marginBottom: 2 }}>
        {t('billing.currentPlan', undefined, { defaultValue: 'Current plan' })}
      </div>
      {/* Kept at/below the section-heading size (18px) so the plan value never
          outweighs the card title. */}
      {/* The server's name, which carries the usage pack — the local map only
          knows base tiers, so an Extended subscriber read "Pro" and had nothing
          telling them the extra usage they pay for arrived. */}
      <div style={{ fontSize: 18, fontWeight: 700 }}>{usage.planName || planLabel(usage.plan)}</div>
      {/* ONE reset date for the whole page: the server anchors the period on the
          subscription's renewal day — the same window the allowance meter,
          the charge cap and the metered total use. A daily allowance says
          "resets daily" on the meter instead, so no second date shows here. */}
      {usage.aiMeter?.window !== 'daily' && (
        <div
          style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}
          data-mol-id="billing-period-resets"
        >
          {t(
            'billing.periodResetsOn',
            { date: periodResetDate(usage) },
            { defaultValue: 'Resets on {{date}}' },
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Opens Stripe's hosted billing portal — the one surface where a subscriber
 * updates their card, reads invoices, and CANCELS (cancellation comes back as
 * a `customer.subscription.deleted` webhook, which drops the account to free).
 *
 * A hook rather than a private helper because two surfaces need the same
 * action: "Manage billing" here, and the pricing page's "Cancel plan" on the
 * Free card — a subscriber who opens pricing to leave must not have to hunt
 * through settings for the exit.
 *
 * The portal opens in a NEW TAB, because these surfaces also render inside the
 * in-IDE settings/upgrade modals where navigating the workspace tab would
 * discard the user's session state. Stripe returns them to the page they came
 * from.
 *
 * @returns The opener, whether a request is in flight, and the signed-in user's
 *   id (`undefined` when signed out — there is then no portal to open).
 */
export function useBillingPortal(): {
  openPortal: () => Promise<void>
  pending: boolean
  userId: string | undefined
} {
  const http = useHttpClient()
  const { state } = useAuth()
  const [pending, setPending] = useState(false)

  // The router validates every `:id` param as a UUID before auth runs, so the
  // `me` alias 400s here — send the real id, as the pricing page does.
  const userId = (state.user as { id?: string } | null)?.id

  const openPortal = async () => {
    if (!userId) return
    setPending(true)
    try {
      const res = await http.post<{ url?: string }>(`/users/${userId}/billing-portal/stripe`, {
        returnPath: `${window.location.pathname}${window.location.search}`,
      })
      const url = res.data?.url
      // Only ever hand the browser a Stripe-hosted portal URL.
      const host = url ? new URL(url).hostname : null
      if (!url || !(host === 'stripe.com' || host?.endsWith('.stripe.com'))) {
        throw new Error(`Unexpected billing portal URL host: ${host ?? 'none'}`)
      }
      // Not `noopener` in the features string: it makes window.open return null even when
      // the tab opens. The host was checked above to be Stripe's; sever the opener by hand.
      const win = window.open(url, '_blank')
      if (win) win.opener = null
      else {
        // The popup ran after an await, outside the user-gesture window. The
        // banner's own click IS a gesture, so its link opens fine.
        emitNotification('Your browser blocked the billing window.', 'warning', {
          label: 'Open billing',
          href: url,
          external: true,
        })
      }
    } catch (error) {
      reportHandledFailure('billing-portal', error)
      emitNotification('Could not open billing. Please try again.', 'error')
    }
    setPending(false)
  }

  return { openPortal, pending, userId }
}

/**
 * "Manage billing" — opens Stripe's hosted billing portal
 * ({@link useBillingPortal}). Renders only for an account that PAYS: a free
 * user has no Stripe customer, so the route would 404, and neither does a
 * teammate on a granted seat — the subscription behind their plan is the
 * owner's.
 *
 * @param props - The loaded usage payload (its plan key decides whether to render).
 */
export function ManageBillingButton({ usage }: { usage: UsageResponseWithBilling }) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const { openPortal, pending, userId } = useBillingPortal()

  if (!userId || !canEnableUsageBilling(usage.plan) || isGrantedSeatPlan(usage.plan)) return null

  return (
    // Secondary: managing an existing subscription is a utility action, and it
    // sits beside "View plans"/"Change plan" — sibling CTAs must not read as the
    // same action (molecule/DESIGN.md → CTA color semantics).
    <Button
      variant="outline"
      color="secondary"
      className={cm.touchTargetCompact}
      onClick={() => void openPortal()}
      disabled={pending}
      data-mol-id="billing-manage"
    >
      {t('billing.manage', undefined, { defaultValue: 'Manage billing' })}
    </Button>
  )
}

/**
 * Extra usage opt-in + monthly spend cap (docs/BAAS-pricing.md §3). Card
 * chrome is the host's.
 *
 * @param props - The usage payload and a reload for post-change refresh.
 */
export function ExtraUsageControls({
  usage,
  reload,
}: {
  usage: UsageResponseWithBilling
  reload: () => Promise<void>
}) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const http = useHttpClient()
  // Sub-16px focusable inputs make iOS Safari zoom the page on focus, so the
  // spend-cap input goes to 16px whenever the viewport is narrow OR touch-first.
  const isMobile = useIsMobile()
  const coarsePointer = useMediaQuery('(pointer: coarse)')

  // Usage-based-billing opt-in: optimistic local state seeded from the usage
  // response, with a pending flag (disable the switch) and an inline error.
  const [usageBilling, setUsageBilling] = useState(usageBillingEnabledFrom(usage))
  const [usageBillingPending, setUsageBillingPending] = useState(false)
  const [usageBillingError, setUsageBillingError] = useState(false)
  useEffect(() => setUsageBilling(usageBillingEnabledFrom(usage)), [usage])

  // Spend-cap control: dollars input + pending/error state. The resolved cap
  // and custom/default flag come from the usage response; a successful PATCH
  // re-fetches usage so the server stays the source of truth for the cap.
  const [capInput, setCapInput] = useState('')
  const [capPending, setCapPending] = useState(false)
  const [capError, setCapError] = useState<'invalid' | 'request' | null>(null)

  // Add-funds control. The balance is real money, so this NEVER optimistically
  // credits: the input clears and the figures re-fetch only after the server
  // confirms the card actually settled.
  const [topUpInput, setTopUpInput] = useState('')
  const [topUpPending, setTopUpPending] = useState(false)
  const [topUpError, setTopUpError] = useState<string | null>(null)
  // A SERVER refusal, kept whole. Flattening these to one "Check your card and
  // try again" told a user with no card on file to check a card they don't
  // have, told a 3DS challenge to retry instead of approve, and told the
  // per-period velocity rail — which refuses every retry by design — to retry.
  // The server already writes the right sentence for each; render it.
  const [topUpFailure, setTopUpFailure] = useState<TopUpFailure | null>(null)

  // Auto-refill toggle — optimistic like the opt-in switch (it persists a
  // preference, it does not move money).
  const [autoRefill, setAutoRefill] = useState(autoRefillFrom(usage).enabled)
  const [autoRefillPending, setAutoRefillPending] = useState(false)
  const [autoRefillError, setAutoRefillError] = useState(false)
  // Amount + trigger inputs. Left EMPTY when the user has not overridden them,
  // so the placeholder shows the value actually in effect rather than the input
  // pretending they chose it.
  const [refillAmountInput, setRefillAmountInput] = useState('')
  const [refillThresholdInput, setRefillThresholdInput] = useState('')
  useEffect(() => setAutoRefill(autoRefillFrom(usage).enabled), [usage])

  // Per-charge bounds the server enforces; the auto-refill amount is a real
  // card charge, so it must satisfy them too.
  const { minCents: topUpMin, maxCents: topUpMax } = topUpLimitsFrom(usage)

  const canToggleUsageBilling = canEnableUsageBilling(usage.plan)

  const handleToggleUsageBilling = useCallback(
    async (next: boolean) => {
      if (!canToggleUsageBilling) return
      setUsageBillingPending(true)
      setUsageBillingError(false)
      // Optimistic: reflect immediately, reconcile with the server's answer.
      setUsageBilling(next)
      try {
        const confirmed = await runBillingAction(http, setUsageBillingAction(next))
        setUsageBilling(confirmed)
      } catch (error) {
        // Revert on failure.
        console.error('Failed to update usage-based billing', error)
        setUsageBilling(!next)
        setUsageBillingError(true)
      } finally {
        setUsageBillingPending(false)
      }
    },
    [http, canToggleUsageBilling],
  )

  const handleSetSpendCap = useCallback(async () => {
    const parsed = parseSpendCapDollars(capInput)
    if (!parsed.ok) {
      setCapError('invalid')
      return
    }
    setCapError(null)
    setCapPending(true)
    try {
      await runSpendCapAction(http, setSpendCapAction(parsed.cents))
      setCapInput('')
      // Re-fetch so the resolved cap + overage figures come from the server.
      await reload()
    } catch (error) {
      console.error('Failed to update the spend cap', error)
      setCapError('request')
    } finally {
      setCapPending(false)
    }
  }, [capInput, http, reload])

  const handleResetSpendCap = useCallback(async () => {
    setCapError(null)
    setCapPending(true)
    try {
      // null = clear the override; the tier default applies again.
      await runSpendCapAction(http, setSpendCapAction(null))
      setCapInput('')
      await reload()
    } catch (error) {
      console.error('Failed to reset the spend cap', error)
      setCapError('request')
    } finally {
      setCapPending(false)
    }
  }, [http, reload])

  const handleTopUp = useCallback(async () => {
    setTopUpFailure(null)
    const parsed = parseTopUpDollars(topUpInput, usage)
    if (!parsed.ok) {
      setTopUpError(parsed.reason)
      return
    }
    setTopUpError(null)
    setTopUpPending(true)
    try {
      await runTopUpAction(http, topUpAction(parsed.cents))
      setTopUpInput('')
      // Re-fetch so the balance shown is the server's, not one we assumed.
      await reload()
    } catch (error) {
      console.error('Failed to add funds', error)
      setTopUpFailure(topUpFailureFrom(error))
    } finally {
      setTopUpPending(false)
    }
  }, [topUpInput, usage, http, reload])

  const handleToggleAutoRefill = useCallback(
    async (next: boolean) => {
      setAutoRefillPending(true)
      setAutoRefill(next)
      try {
        await runBillingAction(http, setAutoRefillAction({ enabled: next }))
        await reload()
      } catch (error) {
        console.error('Failed to update auto-refill', error)
        setAutoRefill(!next)
      } finally {
        setAutoRefillPending(false)
      }
    },
    [http, reload],
  )

  /**
   * Persist the user's own auto-refill amount and/or trigger balance. Each
   * field is sent only when they typed something, so leaving one blank keeps
   * whatever is currently in effect instead of silently resetting it.
   */
  const handleSaveAutoRefill = useCallback(async () => {
    const amount = refillAmountInput.trim()
      ? parseTopUpAmountDollars(refillAmountInput, { minCents: topUpMin, maxCents: topUpMax })
      : null
    const threshold = refillThresholdInput.trim() ? parseDollarsToCents(refillThresholdInput) : null
    if ((amount !== null && !amount.ok) || (threshold !== null && !threshold.ok)) {
      setAutoRefillError(true)
      return
    }
    setAutoRefillError(false)
    setAutoRefillPending(true)
    try {
      await runBillingAction(
        http,
        setAutoRefillAction({
          ...(amount?.ok ? { amountCents: amount.cents } : {}),
          ...(threshold?.ok ? { thresholdCents: threshold.cents } : {}),
        }),
      )
      setRefillAmountInput('')
      setRefillThresholdInput('')
      // Re-read so the labels show the server's resolved values.
      await reload()
    } catch (error) {
      console.error('Failed to save auto-refill settings', error)
      setAutoRefillError(true)
    } finally {
      setAutoRefillPending(false)
    }
  }, [refillAmountInput, refillThresholdInput, topUpMin, topUpMax, http, reload])

  // Extra usage rate-card figures — from the usage response's `billing`
  // block (the effective tier's published rates), never hardcoded cents.
  const capCents = resolvedSpendCapCents(usage)
  const customCap = spendCapIsCustom(usage)
  const overageCents = overageCentsThisPeriod(usage)
  const defaultCapLabel = formatCentsCompact(usage.billing?.defaultSpendCapCents ?? 0)
  const balanceCents = usageBalanceCents(usage)
  const heldCents = heldCentsFrom(usage)
  const chargedCents = chargedThisPeriodCents(usage)
  const refill = autoRefillFrom(usage)
  const refillAmountLabel = formatCentsCompact(refill.amountCents)
  const refillThresholdLabel = formatCentsCompact(refill.thresholdCents)

  return (
    <div data-mol-id="usage-billing-card">
      {/* Title row: toggle inline on the RIGHT of the title (matching every
          other settings toggle row), description below. */}
      <Flex align="center" justify="between" style={{ gap: 12 }}>
        <div className={cm.cn(cm.fontWeight('bold'))} style={{ fontSize: 14 }}>
          {/* Deliberately NOT "pay-as-you-go": usage is PREPAID (a balance the
              user funds before spending it), so that name describes a billing
              model this product does not have. */}
          {t('billing.usageBilling.title', undefined, {
            defaultValue: 'Extra usage',
          })}
        </div>
        <Switch
          checked={canToggleUsageBilling && usageBilling}
          onChange={(e) => handleToggleUsageBilling((e.target as HTMLInputElement).checked)}
          disabled={!canToggleUsageBilling || usageBillingPending}
          data-mol-id="usage-billing-toggle"
        />
      </Flex>
      <div>
        <p
          style={{
            fontSize: 13,
            margin: '4px 0 0',
            color: 'var(--mol-color-text-secondary, #aaaaaa)',
            lineHeight: 1.5,
          }}
        >
          {/* No rate figure. This used to read "at {markup}× provider cost",
              which publishes both our cost basis and an exact multiplier the
              user then holds us to — and these rates are expected to move. The
              "Credit-based tools typically charge 2–5×" line that sat below it
              is gone for the same reason, plus it was a competitor claim we
              would have to keep true. */}
          {t('billing.usageBilling.explanation', undefined, {
            defaultValue:
              'Keep building past your included AI budget, charged from your prepaid balance.',
          })}
        </p>
        <p
          style={{
            fontSize: 13,
            margin: '4px 0 0',
            color: 'var(--mol-color-text-secondary, #aaaaaa)',
            lineHeight: 1.5,
          }}
          data-mol-id="usage-billing-trust"
        >
          {t('billing.usageBilling.trust', undefined, {
            defaultValue: 'We pause at your spend cap — never a surprise bill.',
          })}
        </p>
        {!canToggleUsageBilling && (
          <p
            style={{
              fontSize: 12,
              margin: '8px 0 0',
              color: 'var(--mol-color-text-secondary, #aaaaaa)',
            }}
            data-mol-id="usage-billing-paid-note"
          >
            {t('billing.usageBilling.requiresPaid', undefined, {
              defaultValue: "You'll need to upgrade your plan first.",
            })}
          </p>
        )}
        {usageBillingError && (
          <p
            style={{
              fontSize: 12,
              marginTop: 8,
              marginBottom: 0,
              color: 'var(--mol-color-error, #ef4444)',
            }}
            data-mol-id="usage-billing-error"
          >
            {t('billing.usageBilling.error', undefined, {
              defaultValue: 'Could not update usage-based billing. Please try again.',
            })}
          </p>
        )}
      </div>

      {/* Prepaid balance — the money that actually funds metered usage. Shown
          FIRST because it is what a user needs to act on: the cap below only
          bounds how much may be charged, this is what is available now. */}
      {canToggleUsageBilling && (
        <div
          style={{ borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 16 }}
          data-mol-id="usage-balance-control"
        >
          <Flex
            align="center"
            justify="between"
            style={{ fontSize: 13, marginBottom: 10, flexWrap: 'wrap', gap: 8 }}
          >
            <span className={cm.cn(cm.fontWeight('bold'))} style={{ fontSize: 14 }}>
              {t('billing.balance.title', undefined, { defaultValue: 'Usage balance' })}
            </span>
            <span
              style={{
                fontSize: 16,
                color:
                  balanceCents - heldCents > 0
                    ? 'var(--mol-color-text-primary, inherit)'
                    : 'var(--mol-color-error, #ef4444)',
              }}
              className={cm.cn(cm.fontWeight('bold'))}
              data-mol-id="usage-balance-value"
            >
              {formatCents(balanceCents)}
            </span>
          </Flex>
          {balanceCents < 0 && (
            <div
              data-mol-id="usage-balance-negative"
              className={cm.cn(cm.textSize('sm'), cm.textError)}
              style={{ marginTop: 4 }}
            >
              {t('billing.balanceNegative', undefined, {
                defaultValue:
                  'A refund or a usage charge took your balance below zero. Add funds to keep building.',
              })}
            </div>
          )}
          {heldCents > 0 && (
            <div
              style={{
                fontSize: 12,
                margin: '-6px 0 10px',
                color: 'var(--mol-color-text-secondary, #aaaaaa)',
              }}
              data-mol-id="usage-balance-reserved"
            >
              {t(
                'billing.balance.reserved',
                { amount: formatCents(heldCents) },
                { defaultValue: '{{amount}} reserved for work in progress' },
              )}
            </div>
          )}

          <p
            style={{
              fontSize: 13,
              margin: '0 0 10px',
              color: 'var(--mol-color-text-secondary, #aaaaaa)',
              lineHeight: 1.5,
            }}
            data-mol-id="usage-balance-explanation"
          >
            {t('billing.balance.explanation', undefined, {
              defaultValue: 'Usage past your included AI budgets draws on this balance.',
            })}
          </p>

          <Flex align="center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Input
              size="sm"
              align="left"
              data-mol-id="usage-balance-input"
              value={topUpInput}
              onChange={(e) => setTopUpInput((e.target as HTMLInputElement).value)}
              placeholder={t('billing.balance.placeholder', undefined, {
                defaultValue: 'Amount in dollars, e.g. 20',
              })}
              disabled={topUpPending}
              className={cm.touchTargetCompact}
              style={{ maxWidth: 220, ...(isMobile || coarsePointer ? { fontSize: 16 } : null) }}
            />
            <Button
              size="sm"
              className={cm.touchTargetCompact}
              style={{ height: SM_INPUT_HEIGHT }}
              onClick={() => void handleTopUp()}
              disabled={topUpPending || topUpInput.trim() === ''}
              data-mol-id="usage-balance-add"
            >
              {t('billing.balance.add', undefined, { defaultValue: 'Add funds' })}
            </Button>
          </Flex>
          {(topUpError || topUpFailure) && (
            <div
              style={{
                fontSize: 12,
                marginTop: 8,
                color: 'var(--mol-color-error, #ef4444)',
              }}
              data-mol-id="usage-balance-error"
            >
              <p style={{ margin: 0 }}>
                {topUpFailure
                  ? // The server's own sentence when it sent one — it names the
                    // real obstacle. The per-reason fallbacks below only cover
                    // the refusals the API answers without a message.
                    (topUpFailure.message ?? topUpFailureFallback(t, topUpFailure.reason))
                  : topUpError === 'overCap'
                    ? t('billing.balance.errorOverCap', undefined, {
                        defaultValue:
                          'That would pass your monthly spend cap. Raise the cap below to add more.',
                      })
                    : topUpError === 'belowMinimum' || topUpError === 'aboveMaximum'
                      ? t(
                          'billing.balance.errorRange',
                          { min: formatCentsCompact(topUpMin), max: formatCentsCompact(topUpMax) },
                          {
                            defaultValue: `Enter an amount between ${formatCentsCompact(topUpMin)} and ${formatCentsCompact(topUpMax)}.`,
                          },
                        )
                      : t('billing.balance.errorInvalid', undefined, {
                          defaultValue: 'Enter a dollar amount, e.g. 20.',
                        })}
              </p>
              {topUpFailure?.reason === 'no-customer' && (
                // There is no payment method to charge, and no customer record
                // either — so the billing portal (which needs one) 404s here.
                // Checkout is the only path that creates one, and `/pricing` is
                // intercepted into the modal's pricing view, so the user stays
                // in place. See the report note: the product has no dedicated
                // "add a card" screen for an account with no customer yet.
                <a
                  data-mol-id="usage-balance-add-payment-method"
                  href="/pricing"
                  className={cm.cn(
                    cm.button({ color: 'primary', size: 'sm' }),
                    cm.touchTargetCompact,
                  )}
                  style={{ marginTop: 8, textDecoration: 'none' }}
                >
                  {addPaymentMethodLabel()}
                </a>
              )}
            </div>
          )}

          {/* Auto-refill — "never stop building" without handing the card an
              unbounded mandate: one increment per top-up, capped per period.
              Both numbers are the user's to choose; the amount starts from what
              they last topped up, so the default is their own habit. */}
          <Flex
            align="center"
            justify="between"
            style={{ gap: 12, marginTop: 14, flexWrap: 'wrap' }}
          >
            <span style={{ fontSize: 13 }}>
              {t(
                'billing.autoRefill.label',
                { amount: refillAmountLabel, threshold: refillThresholdLabel },
                {
                  defaultValue: `Add ${refillAmountLabel} automatically when the balance drops below ${refillThresholdLabel}`,
                },
              )}
            </span>
            <Switch
              checked={autoRefill}
              onChange={(e) => handleToggleAutoRefill((e.target as HTMLInputElement).checked)}
              disabled={autoRefillPending}
              data-mol-id="auto-refill-toggle"
            />
          </Flex>

          {autoRefill && (
            <Flex
              align="end"
              style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}
              data-mol-id="auto-refill-settings"
            >
              <label style={{ fontSize: 12, display: 'block' }}>
                <span
                  style={{
                    display: 'block',
                    marginBottom: 4,
                    color: 'var(--mol-color-text-secondary, #aaaaaa)',
                  }}
                >
                  {t('billing.autoRefill.amountLabel', undefined, { defaultValue: 'Top up by' })}
                </span>
                <Input
                  size="sm"
                  align="left"
                  data-mol-id="auto-refill-amount"
                  value={refillAmountInput}
                  onChange={(e) => setRefillAmountInput((e.target as HTMLInputElement).value)}
                  placeholder={refillAmountLabel}
                  disabled={autoRefillPending}
                  className={cm.touchTargetCompact}
                  style={{
                    maxWidth: 130,
                    ...(isMobile || coarsePointer ? { fontSize: 16 } : null),
                  }}
                />
              </label>
              <label style={{ fontSize: 12, display: 'block' }}>
                <span
                  style={{
                    display: 'block',
                    marginBottom: 4,
                    color: 'var(--mol-color-text-secondary, #aaaaaa)',
                  }}
                >
                  {t('billing.autoRefill.thresholdLabel', undefined, {
                    defaultValue: 'When below',
                  })}
                </span>
                <Input
                  size="sm"
                  align="left"
                  data-mol-id="auto-refill-threshold"
                  value={refillThresholdInput}
                  onChange={(e) => setRefillThresholdInput((e.target as HTMLInputElement).value)}
                  placeholder={refillThresholdLabel}
                  disabled={autoRefillPending}
                  className={cm.touchTargetCompact}
                  style={{
                    maxWidth: 130,
                    ...(isMobile || coarsePointer ? { fontSize: 16 } : null),
                  }}
                />
              </label>
              <Button
                size="sm"
                variant="outline"
                className={cm.touchTargetCompact}
                style={{ height: SM_INPUT_HEIGHT }}
                onClick={() => void handleSaveAutoRefill()}
                disabled={
                  autoRefillPending ||
                  (refillAmountInput.trim() === '' && refillThresholdInput.trim() === '')
                }
                data-mol-id="auto-refill-save"
              >
                {t('billing.autoRefill.save', undefined, { defaultValue: 'Save' })}
              </Button>
            </Flex>
          )}
          {autoRefillError && (
            <p
              style={{
                fontSize: 12,
                marginTop: 8,
                marginBottom: 0,
                color: 'var(--mol-color-error, #ef4444)',
              }}
              data-mol-id="auto-refill-error"
            >
              {t(
                'billing.autoRefill.error',
                { min: formatCentsCompact(topUpMin), max: formatCentsCompact(topUpMax) },
                {
                  defaultValue: `Top-up amount must be between ${formatCentsCompact(topUpMin)} and ${formatCentsCompact(topUpMax)}.`,
                },
              )}
            </p>
          )}
        </div>
      )}

      {/* Spend cap — the trust rail: card charges for usage stop at this
            monthly cap. Paid plans only (free is hard-capped). */}
      {canToggleUsageBilling && (
        <div
          style={{
            borderTop: '1px solid var(--color-border)',
            marginTop: 16,
            paddingTop: 16,
          }}
          data-mol-id="spend-cap-control"
        >
          <Flex
            align="center"
            justify="between"
            style={{ fontSize: 13, marginBottom: 10, flexWrap: 'wrap', gap: 8 }}
          >
            <span className={cm.cn(cm.fontWeight('bold'))} style={{ fontSize: 14 }}>
              {t('billing.spendCap.title', undefined, {
                defaultValue: 'Monthly spend cap',
              })}
            </span>
            <span
              style={{ color: 'var(--mol-color-text-secondary, #aaaaaa)' }}
              data-mol-id="spend-cap-value"
            >
              {customCap
                ? t(
                    'billing.spendCap.customValue',
                    { amount: formatCents(capCents) },
                    { defaultValue: `${formatCents(capCents)} (custom)` },
                  )
                : t(
                    'billing.spendCap.defaultValue',
                    { amount: formatCents(capCents) },
                    { defaultValue: `${formatCents(capCents)} (tier default)` },
                  )}
            </span>
          </Flex>

          {/* Charged to the card this period vs the cap. This measures MONEY
              CAPTURED, not usage — the cap's whole promise is about what we
              charge, and metered usage this period is shown separately. */}
          <UsageBar
            label={t('billing.spendCap.usageLabel', undefined, {
              defaultValue: 'Charged this period',
            })}
            value={chargedCents}
            limit={capCents}
            money
          />
          <p
            style={{
              fontSize: 12,
              margin: '0 0 10px',
              color: 'var(--mol-color-text-secondary, #aaaaaa)',
            }}
            data-mol-id="spend-cap-metered"
          >
            {t(
              'billing.spendCap.meteredNote',
              { amount: formatCents(overageCents) },
              { defaultValue: `${formatCents(overageCents)} of metered usage this period.` },
            )}
          </p>

          <Flex align="center" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Input
              size="sm"
              align="left"
              data-mol-id="spend-cap-input"
              value={capInput}
              onChange={(e) => setCapInput((e.target as HTMLInputElement).value)}
              placeholder={t('billing.spendCap.placeholder', undefined, {
                defaultValue: 'Custom cap in dollars, e.g. 50',
              })}
              disabled={capPending}
              className={cm.touchTargetCompact}
              // sm resolves to 15px — below the 16px iOS zoom-on-focus
              // threshold, so hold 16px on narrow/touch viewports only
              style={{ maxWidth: 220, ...(isMobile || coarsePointer ? { fontSize: 16 } : null) }}
            />
            <Button
              size="sm"
              className={cm.touchTargetCompact}
              style={{ height: SM_INPUT_HEIGHT }}
              onClick={() => void handleSetSpendCap()}
              disabled={capPending || capInput.trim() === ''}
              data-mol-id="spend-cap-set"
            >
              {t('billing.spendCap.set', undefined, { defaultValue: 'Set cap' })}
            </Button>
            {customCap && (
              <Button
                size="sm"
                variant="outline"
                className={cm.touchTargetCompact}
                style={{ height: SM_INPUT_HEIGHT }}
                onClick={() => void handleResetSpendCap()}
                disabled={capPending}
                data-mol-id="spend-cap-reset"
              >
                {t(
                  'billing.spendCap.reset',
                  { amount: defaultCapLabel },
                  { defaultValue: `Reset to default (${defaultCapLabel})` },
                )}
              </Button>
            )}
          </Flex>
          {capError && (
            <p
              style={{
                fontSize: 12,
                marginTop: 8,
                marginBottom: 0,
                color: 'var(--mol-color-error, #ef4444)',
              }}
              data-mol-id="spend-cap-error"
            >
              {capError === 'invalid'
                ? t('billing.spendCap.invalid', undefined, {
                    defaultValue:
                      'Enter a whole-cent amount between $0 and $10,000 (e.g. 50 or 12.50).',
                  })
                : t('billing.spendCap.error', undefined, {
                    defaultValue: 'Could not update your spend cap. Please try again.',
                  })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Red banner shown when any resource limit is exceeded. Renders nothing
 * otherwise.
 *
 * @param props - The usage payload.
 */
export function OverLimitBanner({ usage }: { usage: UsageResponseWithBilling }) {
  const { t } = useTranslation()
  const anyOver =
    limitState(usage.deployments, usage.limits.maxActiveDeployments) === 'over' ||
    limitState(usage.customDomains, usage.limits.maxCustomDomains) === 'over' ||
    limitState(usage.storageMb, usage.limits.maxStorageMb) === 'over'
  if (!anyOver) return null
  return (
    <div
      style={{
        padding: '10px 14px',
        marginBottom: 16,
        borderRadius: 8,
        background: 'rgba(239,68,68,0.08)',
        border: '1px solid rgba(239,68,68,0.2)',
        color: 'var(--mol-color-error, #ef4444)',
        fontSize: 13,
      }}
      data-mol-id="billing-over-limit"
    >
      {t('billing.overLimit', undefined, {
        defaultValue:
          'You have reached one or more plan limits. Upgrade to keep building without interruption.',
      })}
    </div>
  )
}

/**
 * Resource usage bars (live apps, custom domains, storage). Card chrome and
 * headings are the host's.
 *
 * @param props - The usage payload.
 */
export function ResourceUsageBars({ usage }: { usage: UsageResponseWithBilling }) {
  const { t } = useTranslation()
  return (
    <div data-mol-id="billing-limits">
      <UsageBar
        label={t('billing.deployments', undefined, { defaultValue: 'Live apps' })}
        value={usage.deployments}
        limit={usage.limits.maxActiveDeployments}
      />
      <UsageBar
        label={t('billing.customDomains', undefined, { defaultValue: 'Custom domains' })}
        value={usage.customDomains}
        limit={usage.limits.maxCustomDomains}
      />
      <UsageBar
        label={t('billing.storage', undefined, { defaultValue: 'Storage' })}
        value={usage.storageMb}
        limit={usage.limits.maxStorageMb}
        storage
      />
    </div>
  )
}

/**
 * The user-facing AI usage meter (0–100% of the allowance) — AI usage is never
 * rendered as a currency amount; money appears only for actual payments
 * (subscription, spend cap, metered overage). The window VARIES by tier
 * (unpaid: rolling day; paid: billing period), so every label reads
 * `aiMeter.window` rather than assuming a day. Renders nothing when the tier
 * is unmetered or the payload carries no meter.
 *
 * @param props - The usage payload.
 */
export function AiUsageMeter({ usage }: { usage: UsageResponseWithBilling }) {
  const { t } = useTranslation()
  if (!usage.aiMeter || usage.aiMeter.unmetered) return null
  return (
    <div data-mol-id="billing-ai-usage">
      <Flex align="center" justify="between" style={{ fontSize: 13, marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>
          {usage.aiMeter.window === 'billing-period'
            ? t('billing.aiUsage.thisPeriod', undefined, {
                defaultValue: 'This billing period',
              })
            : t('billing.aiUsage.today', undefined, { defaultValue: 'Today' })}
        </span>
        <span style={{ color: 'var(--mol-color-text-secondary, #aaaaaa)' }}>
          {usage.aiMeter.window === 'billing-period'
            ? t(
                'billing.aiUsage.percentUsedPeriod',
                { percent: usage.aiMeter.usedPercent },
                // "Monthly", not "this period": true for a yearly subscriber too
                // (the allowance refreshes monthly on the renewal day), and the
                // same phrase the nudge, the inspector and /cost use.
                { defaultValue: '{{percent}}% of your monthly allowance' },
              )
            : t(
                'billing.aiUsage.percentUsed',
                { percent: usage.aiMeter.usedPercent },
                { defaultValue: '{{percent}}% of your daily allowance' },
              )}
        </span>
      </Flex>
      <div style={meterTrackStyle}>
        <div
          style={{
            ...meterFillStyle,
            width: `${Math.max(0, Math.min(100, usage.aiMeter.usedPercent))}%`,
            background:
              usage.aiMeter.usedPercent >= 100
                ? 'var(--color-error, #e5484d)'
                : 'var(--color-primary)',
          }}
        />
      </div>
      <p style={{ fontSize: 12, opacity: 0.6, margin: '8px 0 0', lineHeight: 1.5 }}>
        {usage.aiMeter.window === 'billing-period'
          ? t(
              'billing.aiUsage.notePeriodOn',
              { date: periodResetDate(usage) },
              { defaultValue: 'Synthase usage — resets on {{date}}.' },
            )
          : t('billing.aiUsage.note', undefined, {
              defaultValue: 'Synthase usage — resets daily.',
            })}
      </p>
    </div>
  )
}

/**
 * Brokered usage by provider/category, with per-line relative bars. Card
 * chrome and headings are the host's; the totals line renders inline here.
 *
 * @param props - The usage payload.
 */
export function BrokerUsage({ usage }: { usage: UsageResponseWithBilling }) {
  const { t } = useTranslation()
  const broker: BrokerUsageLine[] = usage.broker ?? []
  return (
    <div data-mol-id="billing-broker">
      {broker.length > 0 && (
        <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}>
          {t(
            'billing.brokerTotals',
            { requests: totalBrokerRequests(broker), units: totalBrokerUnits(broker) },
            {
              defaultValue: '{{requests}} requests · {{units}} units',
            },
          )}
        </div>
      )}
      {broker.length === 0 ? (
        <p style={{ fontSize: 13, opacity: 0.6, margin: 0 }}>
          {t('billing.noBrokeredUsage', undefined, {
            defaultValue: 'No brokered API usage this period.',
          })}
        </p>
      ) : (
        broker.map((line) => {
          const max = Math.max(...broker.map((l) => l.requests), 1)
          return (
            <div
              key={`${line.provider}.${line.category}`}
              style={{ marginBottom: 12 }}
              data-mol-id={`broker-${line.provider}-${line.category}`}
            >
              <Flex align="center" justify="between" style={{ fontSize: 13, marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{brokerLabel(line)}</span>
                <span style={{ color: 'var(--mol-color-text-secondary, #aaaaaa)' }}>
                  {t(
                    'billing.brokerLine',
                    { requests: line.requests, units: line.units },
                    {
                      defaultValue: '{{requests}} req · {{units}} units',
                    },
                  )}
                </span>
              </Flex>
              <div style={meterTrackStyle}>
                <div
                  style={{
                    ...meterFillStyle,
                    width: `${Math.min((line.requests / max) * 100, 100)}%`,
                    background: 'var(--color-primary-light, #4070e0)',
                  }}
                />
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

/** Props for {@link BillingContent}. */
export interface BillingContentProps {
  /**
   * Where the upgrade CTAs go. The in-IDE upgrade modal switches to its
   * pricing view (the IDE tab must never navigate).
   */
  onShowPricing: () => void
}

/**
 * The full usage & billing body (plan card, extra usage + spend cap, usage
 * cards) — the in-IDE upgrade modal's billing view. Composed from the same
 * exported pieces the settings panel uses.
 */
export function BillingContent({ onShowPricing }: BillingContentProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const { usage, loading, error, reload } = useAccountUsage()

  return (
    <>
      <Flex
        align="center"
        justify="between"
        className={cm.sp('mb', 4)}
        style={{ flexWrap: 'wrap', gap: 12 }}
      >
        <h1
          className={cm.cn(cm.textSize('2xl'), cm.fontWeight('bold'))}
          data-mol-id="billing-title"
        >
          {t('billing.title', undefined, { defaultValue: 'Usage & Billing' })}
        </h1>
        <Button
          color="primary"
          className={cm.touchTargetCompact}
          onClick={onShowPricing}
          data-mol-id="billing-upgrade"
        >
          {t('billing.upgrade', undefined, { defaultValue: 'Upgrade plan' })}
        </Button>
      </Flex>

      {loading ? (
        <Flex align="center" justify="center" className={cm.sp('py', 12)}>
          <MoleculeSpinner />
        </Flex>
      ) : error || !usage ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ color: 'var(--mol-color-error, #ef4444)', fontSize: 15, marginBottom: 16 }}>
            {t('billing.loadError', undefined, { defaultValue: 'Failed to load usage data.' })}
          </p>
          <Button variant="outline" className={cm.touchTargetCompact} onClick={() => void reload()}>
            {t('common.retry', undefined, { defaultValue: 'Retry' })}
          </Button>
        </div>
      ) : (
        <>
          {/* Current plan */}
          <div
            data-mol-id="billing-plan-card"
            style={{
              ...cardStyle,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
            }}
          >
            <PlanSummary usage={usage} />
            <Flex align="center" gap="sm" style={{ flexWrap: 'wrap' }}>
              <ManageBillingButton usage={usage} />
              <Button variant="outline" className={cm.touchTargetCompact} onClick={onShowPricing}>
                {t('billing.viewPlans', undefined, { defaultValue: 'View plans' })}
              </Button>
            </Flex>
          </div>

          {/* Extra usage opt-in + spend cap */}
          <div style={cardStyle}>
            <ExtraUsageControls usage={usage} reload={reload} />
          </div>

          <OverLimitBanner usage={usage} />

          {/* Resource limits */}
          <div style={cardStyle}>
            <div className={cm.cn(cm.fontWeight('bold'), cm.sp('mb', 4))} style={{ fontSize: 14 }}>
              {t('billing.resources', undefined, { defaultValue: 'Resource usage' })}
            </div>
            <ResourceUsageBars usage={usage} />
          </div>

          {/* AI usage meter */}
          {usage.aiMeter && !usage.aiMeter.unmetered && (
            <div style={cardStyle}>
              <div
                className={cm.cn(cm.fontWeight('bold'), cm.sp('mb', 4))}
                style={{ fontSize: 14 }}
              >
                {t('billing.aiUsage.title', undefined, { defaultValue: 'AI usage' })}
              </div>
              <AiUsageMeter usage={usage} />
            </div>
          )}

          {/* Brokered usage by provider/category */}
          <div style={{ ...cardStyle, marginBottom: 48 }}>
            <div className={cm.cn(cm.fontWeight('bold'), cm.sp('mb', 4))} style={{ fontSize: 14 }}>
              {t('billing.brokeredUsage', undefined, { defaultValue: 'Brokered usage' })}
            </div>
            <BrokerUsage usage={usage} />
          </div>
        </>
      )}
    </>
  )
}
